import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import './CreateListing.css';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Header from '../../components/ui/Header';
import PaymentModal from '../../components/ui/PaymentModal';

// Import components
import PhotoUpload from './components/PhotoUpload';
import CategorySelector from './components/CategorySelector';
import PricingSection from './components/PricingSection';
import VisibilitySelector from './components/VisibilitySelector';
import LocationInput from './components/LocationInput';
import ContactInfo from './components/ContactInfo';
import ListingPreview from './components/ListingPreview';

// Configuration Axios
import axios from 'axios';
const API_BASE_URL = 'https://api.autocasses.com';
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

axiosInstance.interceptors.response.use(
  response => response,
  error => {
    console.error('Erreur API:', error);
    
    if (error.code === 'ECONNABORTED') {
      throw new Error('Le serveur met trop de temps à répondre. Veuillez réessayer.');
    }
    
    if (!error.response) {
      throw new Error('Impossible de se connecter au serveur. Vérifiez votre connexion internet.');
    }
    
    throw error;
  }
);

// Composant DescriptionStep défini en dehors pour éviter les re-renders inutiles
const DescriptionStep = ({ 
  description, 
  validationErrors, 
  setDescription, 
  setValidationErrors, 
  handleRemoveFeature, 
  handleAddFeature 
}) => {
  // Gestionnaires locaux pour une meilleure performance
  const handleTitleChangeLocal = useCallback((e) => {
    const value = e.target.value;
    setDescription(prev => ({
      ...prev,
      title: value
    }));
    
    // Effacer l'erreur si elle existe
    if (validationErrors.title) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.title;
        return newErrors;
      });
    }
  }, [validationErrors.title, setDescription, setValidationErrors]);

  const handleDescriptionChangeLocal = useCallback((e) => {
    const value = e.target.value;
    setDescription(prev => ({
      ...prev,
      description: value
    }));
    
    // Effacer l'erreur si elle existe
    if (validationErrors.description) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.description;
        return newErrors;
      });
    }
  }, [validationErrors.description, setDescription, setValidationErrors]);

  const handleFeatureChangeLocal = useCallback((index, value) => {
    const newFeatures = [...description.features];
    newFeatures[index] = value;
    setDescription(prev => ({
      ...prev,
      features: newFeatures
    }));
  }, [description.features, setDescription]);

  return (
    <div className="description-container">
      {/* Champ Titre */}
      <div className="text-area-container">
        <label className="label">
          Titre de l'annonce
          <span className="required"> *</span>
        </label>
        <p className="input-description">
          Soyez clair et concis. Mentionnez la marque, le modèle et l'état.
        </p>
        <Input
          type="text"
          placeholder="Ex: Moteur Toyota Corolla 2008 en parfait état"
          value={description.title || ''}
          onChange={handleTitleChangeLocal}
          maxLength={100}
          fullWidth
          error={validationErrors.title}
        />
        <p className="char-count">
          {(description.title || '').length}/100 caractères
        </p>
      </div>
      
      {/* Champ Description */}
      <div className="text-area-container">
        <label className="label">
          Description détaillée
          <span className="required"> *</span>
        </label>
        <textarea
          className={`text-area ${validationErrors.description ? 'error' : ''}`}
          placeholder="Décrivez votre produit en détail. Incluez toutes les informations importantes..."
          value={description.description || ''}
          onChange={handleDescriptionChangeLocal}
          rows={6}
          maxLength={2000}
        />
        {validationErrors.description && (
          <p className="error-message">{validationErrors.description}</p>
        )}
        <p className="char-count">
          {(description.description || '').length}/2000 caractères
        </p>
      </div>
      
      {/* Caractéristiques */}
      <div className="features-container">
        <label className="label">Caractéristiques principales (optionnel)</label>
        {description.features.map((feature, index) => (
          <div key={index} className="feature-row">
            <Input
              type="text"
              placeholder="Ex: Kilométrage bas, Origine France, Garantie 6 mois"
              value={feature || ''}
              onChange={(e) => handleFeatureChangeLocal(index, e.target.value)}
              fullWidth
            />
            <button
              className="delete-button"
              onClick={() => handleRemoveFeature(index)}
              type="button"
            >
              <Icon name="Trash2" size={20} color="#FF3B30" />
            </button>
          </div>
        ))}
        
        {description.features.length < 5 && (
          <button
            className="add-button"
            onClick={handleAddFeature}
            type="button"
          >
            <Icon name="Plus" size={20} color="#007AFF" />
            <span className="add-button-text">
              Ajouter une caractéristique
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

const CreateListing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams();
  
  // Extraire les paramètres de l'URL ou du state de navigation
  const searchParams = new URLSearchParams(location.search);
  const isEditMode = location.state?.isEditMode || searchParams.get('edit') === 'true';
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [lastValidatedStep, setLastValidatedStep] = useState(null);

  // État du formulaire
  const [photos, setPhotos] = useState([]);
  const [category, setCategory] = useState('');
  const [productDetails, setProductDetails] = useState({
    condition: '',
    brand: '',
    model: '',
    year: '',
    mileage: '',
    partNumber: '',
    warranty: '',
    compatibleModels: [],
    sousCategorieId: ''
  });
  const [description, setDescription] = useState({
    title: '',
    description: '',
    features: []
  });
  const [pricing, setPricing] = useState({
    price: '',
    negotiable: false,
    acceptTrade: false,
    premiumUpgrades: []
  });
  const [visibility, setVisibility] = useState({
    level: '',
    price: 0,
    autoRenewal: false
  });
  const [locationState, setLocationState] = useState({
    city: '',
    countryCode: '',
    countryName: '',
    countryFlag: '',
    address: '',
    coordinates: null,
    isGPSDetected: false
  });
  const [contact, setContact] = useState({
    whatsapp: '',
    alternativePhone: '',
    email: '',
    isVerified: false,
    showWhatsApp: true,
    allowCalls: false,
    smsNotifications: false,
    availableFrom: '08:00',
    availableUntil: '18:00',
    preferredLanguage: ''
  });

  const steps = [
    { id: 'photos', title: 'Photos', icon: 'Camera' },
    { id: 'category', title: 'Catégorie', icon: 'Tag' },
    { id: 'description', title: 'Description', icon: 'FileText' },
    { id: 'pricing', title: 'Prix', icon: 'DollarSign' },
    { id: 'location', title: 'Localisation', icon: 'MapPin' },
    { id: 'preview', title: 'Aperçu', icon: 'Eye' }
  ];

  // Gestion du localStorage (remplace AsyncStorage)
  const getLocalStorageItem = useCallback((key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Erreur lecture localStorage:', error);
      return null;
    }
  }, []);

  const setLocalStorageItem = useCallback((key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Erreur écriture localStorage:', error);
    }
  }, []);

  const removeLocalStorageItem = useCallback((key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Erreur suppression localStorage:', error);
    }
  }, []);

  useEffect(() => {
    loadUserData();
    if (isEditMode && productId) {
      loadProductData(productId);
    } else {
      loadDraft();
    }
  }, [isEditMode, productId]);

  const loadUserData = async () => {
    try {
      const userData = getLocalStorageItem('userData');
      if (userData) {
        setUserId(userData.id);
        
        if (userData.phone) {
          setContact(prev => ({
            ...prev,
            whatsapp: userData.phone.replace('+237', '').replace(/\s/g, ''),
            email: userData.email || ''
          }));
        }
      }
    } catch (error) {
      console.error('Erreur chargement données utilisateur:', error);
    }
  };

  const loadProductData = async (id) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/api/produit/produits/${id}`);
      
      if (response.data) {
        const product = response.data;
        
        setDescription({
          title: product.titre || '',
          description: product.description || '',
          features: product.caracteristiques || []
        });
        
        setPricing({
          price: product.prix?.toString() || '',
          negotiable: product.negociable === 'true' || product.negociable === true,
          acceptTrade: product.echange === 'true' || product.echange === true,
          premiumUpgrades: []
        });
        
        setCategory(product.categorieId || product.categorie?.id || '');
        
        setProductDetails({
          condition: product.etat || '',
          brand: product.marque || '',
          model: product.modele || '',
          year: product.annee || '',
          mileage: product.kilometrage || '',
          partNumber: product.numpiece || '',
          warranty: product.garantie || '',
          compatibleModels: product.modeles_compatibles || [],
          sousCategorieId: product.sousCategorieId || ''
        });
        
        setLocationState({
          city: product.city || '',
          countryCode: '',
          countryName: product.pays || '',
          countryFlag: '',
          address: product.adresse_detail || '',
          coordinates: null,
          isGPSDetected: false
        });

        if (product.photos && product.photos.length > 0) {
          const formattedExistingPhotos = product.photos.map((photo_url, index) => {
            let uri = photo_url;
            uri = `${photo_url.photo_url}`;

            return {
              id: `existing_${Date.now()}_${index}`,
              uri: uri,
              url: photo_url.photo_url,
              fileName: `Photo existante ${index + 1}`,
              type: 'image/jpeg',
              isExisting: true
            };
          });
          
          setExistingPhotos(formattedExistingPhotos);
        } else {
          setExistingPhotos([]);
        }
      }
    } catch (error) {
      console.error('Erreur chargement produit:', error);
      alert('Erreur lors du chargement du produit');
    } finally {
      setIsLoading(false);
    }
  };

  const loadDraft = async () => {
    try {
      const draft = getLocalStorageItem('listingDraft');
      if (draft) {
        setPhotos(draft?.photos || []);
        setCategory(draft?.category || '');
        setProductDetails(draft?.productDetails || { condition: '', brand: '', model: '', compatibleModels: [], sousCategorieId: '' });
        setDescription(draft?.description || { title: '', description: '', features: [] });
        setPricing(draft?.pricing || { price: '', negotiable: false, acceptTrade: false, premiumUpgrades: [] });
        setVisibility(draft?.visibility || { level: '', price: 0, autoRenewal: false });
        setLocationState(draft?.location || { city: '', countryCode: '', countryName: '', countryFlag: '', address: '', coordinates: null, isGPSDetected: false });
        setContact(draft?.contact || { whatsapp: '', alternativePhone: '', email: '', isVerified: false, showWhatsApp: true, allowCalls: false, smsNotifications: false, availableFrom: '08:00', availableUntil: '18:00', preferredLanguage: '' });
      }
    } catch (error) {
      console.error('Erreur chargement brouillon:', error);
    }
  };

  const saveDraft = async () => {
    if (isEditMode) return;
    
    try {
      const draft = {
        photos,
        category,
        productDetails,
        description,
        pricing,
        visibility,
        location: locationState,
        contact
      };
      setLocalStorageItem('listingDraft', draft);
    } catch (error) {
      console.error('Erreur sauvegarde brouillon:', error);
    }
  };

  useEffect(() => {
    if (!isEditMode) {
      saveDraft();
    }
  }, [photos, category, productDetails, description, pricing, visibility, locationState, contact, isEditMode]);

  // Validation simple et sans effets de bord
  const validateStep = (stepIndex) => {
    const step = steps[stepIndex];
    const errors = {};
    
    switch (step?.id) {
      case 'photos':
        const hasPhotos = photos.length > 0 || (isEditMode && existingPhotos.length > 0);
        if (!hasPhotos) {
          errors.photos = 'Veuillez ajouter au moins une photo';
        }
        break;
        
      case 'category':
        if (!category) {
          errors.category = 'Veuillez sélectionner une catégorie';
        }
        if (!productDetails?.condition) {
          errors.condition = 'Veuillez spécifier l\'état du produit';
        }
        break;
        
      case 'description':
        if (!description?.title?.trim()) {
          errors.title = 'Veuillez saisir un titre';
        } else if (description.title.length < 10) {
          errors.title = 'Le titre doit contenir au moins 10 caractères';
        }
        if (!description?.description?.trim()) {
          errors.description = 'Veuillez saisir une description';
        } else if (description.description.length < 30) {
          errors.description = 'La description doit contenir au moins 30 caractères';
        }
        break;
        
      case 'pricing':
        if (!pricing?.price) {
          errors.price = 'Veuillez saisir un prix';
        } else if (isNaN(pricing.price) || Number(pricing.price) <= 0) {
          errors.price = 'Veuillez saisir un prix valide';
        }
        break;
        
      case 'location':
        if (!locationState?.city?.trim()) {
          errors.city = 'Veuillez saisir une ville';
        }
        if (!locationState?.countryCode?.trim()) {
          errors.country = 'Veuillez sélectionner un pays';
        }
        break;
        
      case 'preview':
        // Aucune validation nécessaire pour l'aperçu
        break;
        
      default:
        break;
    }
    
    return errors;
  };

  // Validation pour le bouton Suivant
  const handleNext = () => {
    const errors = validateStep(currentStep);
    
    if (Object.keys(errors).length === 0) {
      // Effacer les erreurs de cette étape
      setValidationErrors({});
      setLastValidatedStep(currentStep);
      
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // Afficher les erreurs
      setValidationErrors(errors);
      alert(`Veuillez corriger les erreurs avant de passer à l'étape suivante.\n\n${Object.values(errors).join('\n')}`);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepClick = (stepIndex) => {
    // Permettre de cliquer sur les étapes déjà validées
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const calculateTotalCost = () => {
    let total = 0;
    total += visibility?.price || 0;
    
    if (pricing?.premiumUpgrades?.length < 0) {
      const premiumPricing = category === 'complete_vehicles' 
        ? { basic: 5000, featured: 10000, premium: 20000 }
        : { featured: 1000, premium: 2500 };
      
      pricing.premiumUpgrades.forEach(upgrade => {
        total += premiumPricing?.[upgrade] || 0;
      });
    }
    
    return total;
  };

  const validateAllSteps = () => {
    return steps.every((step, index) => {
      const errors = validateStep(index);
      return Object.keys(errors).length === 0;
    });
  };

  const handleSubmit = async () => {
    if (!validateAllSteps()) {
      alert('Veuillez remplir tous les champs obligatoires dans toutes les étapes');
      return;
    }
    
    if (isEditMode) {
      await sendToBackend();
    } else {
      const totalCost = calculateTotalCost();
      if (totalCost > 0) {
        setPaymentAmount(totalCost);
        setShowPaymentModal(true);
      } else {
        await sendToBackend();
      }
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...description.features];
    newFeatures[index] = value;
    setDescription(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const handleAddFeature = () => {
    if (description.features.length < 5) {
      setDescription(prev => ({
        ...prev,
        features: [...prev.features, '']
      }));
    }
  };

  const handleRemoveFeature = (index) => {
    const newFeatures = description.features.filter((_, i) => i !== index);
    setDescription(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const prepareFormData = () => {
    const formData = new FormData();
    
    formData.append('titre', description.title);
    formData.append('description', description.description);
    formData.append('prix', pricing.price);
    formData.append('garantie', productDetails.warranty || '');
    formData.append('city', locationState.city);
    formData.append('adresse_detail', locationState.address || '');
    formData.append('etat', productDetails.condition);
    formData.append('numpiece', productDetails.partNumber || '');
    formData.append('negociable', pricing.negotiable ? 'true' : 'false');
    formData.append('echange', pricing.acceptTrade ? 'true' : 'false');
    
    if (locationState.countryName) {
      formData.append('pays', locationState.countryName);
    }
    
    if (productDetails.brand) {
      formData.append('marque', productDetails.brand);
    }
    if (productDetails.model) {
      formData.append('modele', productDetails.model);
    }
    if (productDetails.year) {
      formData.append('annee', productDetails.year);
    }
    if (productDetails.mileage) {
      formData.append('kilometrage', productDetails.mileage);
    }
    if (productDetails.compatibleModels && productDetails.compatibleModels.length > 0) {
      formData.append('modeles_compatibles', JSON.stringify(productDetails.compatibleModels));
    }
    
    if (category) {
      formData.append('categorieId', category);
    }
    if (productDetails.sousCategorieId) {
      formData.append('sousCategorieId', productDetails.sousCategorieId);
    }
    
    if (isEditMode && productId) {
      formData.append('id', productId);
    }
    
    // Ajouter les nouvelles photos
    photos.forEach((photo, index) => {
      if (photo.uri && !photo.isExisting) {
        // Convertir data URL en blob
        if (photo.uri.startsWith('data:')) {
          const byteString = atob(photo.uri.split(',')[1]);
          const mimeString = photo.uri.split(',')[0].split(':')[1].split(';')[0];
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: mimeString });
          formData.append('photos', blob, photo.fileName || `photo_${index}.jpg`);
        } else {
          // Pour les URLs
          formData.append('photos_urls[]', photo.uri);
        }
      }
    });
    
    // Ajouter les photos existantes
    if (isEditMode && existingPhotos.length > 0) {
      const existingUrls = existingPhotos.map(photo => photo.url || photo.uri).filter(url => url);
      if (existingUrls.length > 0) {
        formData.append('existing_photos', JSON.stringify(existingUrls));
      }
    }
    
    return formData;
  };

  const sendToBackend = async () => {
    if (!userId) {
      alert('Vous devez être connecté pour publier une annonce');
      return;
    }
    
    setIsSubmitting(true);
    setUploadProgress(0);
    
    try {
      const formData = prepareFormData();
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(progress);
        },
      };
      
      let response;
      if (isEditMode) {
        response = await axiosInstance.put(
          `/api/produit/update/${productId}`,
          formData,
          config
        );
      } else {
        response = await axiosInstance.post(
          `/api/produit/set/${userId}`,
          formData,
          config
        );
      }
      
      if (response.status === 200 || response.status === 201) {
        const successMessage = isEditMode 
          ? 'Produit mis à jour avec succès!' 
          : 'Annonce créée avec succès!';
        
        alert(successMessage);
        
        if (!isEditMode && response.data.id) {
          removeLocalStorageItem('listingDraft');
          setLocalStorageItem('lastProductId', response.data.id.toString());
        }
        
        // Rediriger vers le profil
        navigate('/user-profile');
      } else {
        throw new Error('Réponse inattendue du serveur');
      }
      
    } catch (error) {
      console.error('Erreur API détaillée:', error);
      
      let errorMessage = isEditMode 
        ? 'Erreur lors de la modification du produit'
        : 'Erreur lors de la création de l\'annonce';
      
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 400) {
          errorMessage = data.error || 'Données invalides';
        } else if (status === 404) {
          errorMessage = isEditMode 
            ? 'Produit non trouvé'
            : 'Utilisateur non trouvé';
        } else if (status === 409) {
          errorMessage = data.error || 'Ce produit existe déjà';
        } else if (status === 413) {
          errorMessage = 'Les images sont trop volumineuses';
        } else if (status === 500) {
          errorMessage = 'Erreur serveur';
        }
      } else if (error.request) {
        errorMessage = 'Impossible de se connecter au serveur';
      } else {
        errorMessage = error.message || 'Erreur lors de l\'envoi des données';
      }
      
      alert(errorMessage);
      
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const deleteProduct = async () => {
    if (!productId) {
      alert('ID produit manquant');
      return;
    }
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible.')) {
      try {
        setIsDeleting(true);
        
        const response = await axiosInstance.delete(`/api/produit/delete/${productId}`);
        
        if (response.status === 200) {
          alert('Produit supprimé avec succès');
          navigate('/profile');
        }
      } catch (error) {
        console.error('Erreur suppression:', error);
        alert('Erreur lors de la suppression du produit');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Fonction pour vérifier si on peut passer à l'étape suivante
  const canProceed = () => {
    // Pour le bouton Suivant, on vérifie juste les conditions basiques
    const step = steps[currentStep];
    
    switch (step?.id) {
      case 'photos':
        return photos.length > 0 || (isEditMode && existingPhotos.length > 0);
      case 'category':
        return category && productDetails?.condition;
      case 'description':
        return description?.title?.trim() && description?.description?.trim();
      case 'pricing':
        return pricing?.price && !isNaN(pricing.price);
      case 'location':
        return locationState?.city?.trim() && locationState?.countryCode?.trim();
      case 'preview':
        return true;
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    const step = steps[currentStep];
    
    switch (step?.id) {
      case 'photos':
        return (
          <PhotoUpload
            photos={photos}
            existingPhotos={existingPhotos}
            onPhotosChange={setPhotos}
            onExistingPhotosChange={setExistingPhotos}
            maxPhotos={8}
            isEditMode={isEditMode}
            validationError={validationErrors.photos}
          />
        );
      case 'category':
        return (
          <CategorySelector
            category={category}
            onCategoryChange={setCategory}
            productDetails={productDetails}
            onDetailsChange={setProductDetails}
            isEditMode={isEditMode}
            validationErrors={validationErrors}
          />
        );
      case 'description':
        return (
          <DescriptionStep
            description={description}
            validationErrors={validationErrors}
            setDescription={setDescription}
            setValidationErrors={setValidationErrors}
            handleRemoveFeature={handleRemoveFeature}
            handleAddFeature={handleAddFeature}
          />
        );
      case 'pricing':
        return (
          <PricingSection
            pricing={pricing}
            onPricingChange={setPricing}
            category={category}
            isEditMode={isEditMode}
            validationErrors={validationErrors}
          />
        );
      case 'location':
        return (
          <LocationInput
            location={locationState}
            onLocationChange={setLocationState}
            validationErrors={validationErrors}
          />
        );
      case 'preview':
        return (
          <ListingPreview
            photos={[...existingPhotos, ...photos]}
            category={category}
            productDetails={productDetails}
            description={description}
            pricing={pricing}
            visibility={visibility}
            location={locationState}
            contact={contact}
            isEditMode={isEditMode}
          />
        );
      default:
        return null;
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className="step-indicator-container">
        <div className="step-indicator">
          {steps.map((step, index) => (
            <button
              key={step.id}
              className={`step-item ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              onClick={() => handleStepClick(index)}
              disabled={index > currentStep}
              type="button"
            >
              <div className="step-icon">
                {index < currentStep ? (
                  <Icon name="Check" size={16} color="white" />
                ) : (
                  <Icon name={step.icon} size={16} color={index === currentStep ? 'white' : '#666'} />
                )}
              </div>
              <span className="step-title">{step.title}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderUploadProgress = () => {
    if (!isSubmitting || uploadProgress === 0) return null;
    
    return (
      <div className="upload-overlay">
        <div className="upload-modal">
          <div className="spinner"></div>
          <p className="upload-text">
            {isEditMode 
              ? 'Modification en cours...' 
              : 'Envoi en cours...'}
          </p>
          <p className="upload-percent">{Math.round(uploadProgress)}%</p>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <p className="upload-info">
            Veuillez ne pas quitter cette page pendant l'envoi
          </p>
        </div>
      </div>
    );
  };

  const getStepDescription = (stepId) => {
    const descriptions = {
      photos: 'Ajoutez des photos de qualité pour montrer votre produit',
      category: 'Sélectionnez la catégorie et précisez les détails',
      description: 'Décrivez votre produit de manière détaillée',
      pricing: 'Définissez le prix et les options de paiement',
      location: 'Indiquez où se trouve votre produit',
      preview: 'Vérifiez votre annonce avant publication'
    };
    
    return descriptions[stepId] || '';
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner large"></div>
        <p className="loading-text">
          Chargement des données du produit...
        </p>
      </div>
    );
  }

  return (
    <div className="create-listing-container">
      <Header 
        title={isEditMode ? 'Modifier l\'annonce' : 'Créer une annonce'}
        leftIcon="ArrowLeft"
        onLeftPress={() => {
          if (currentStep === 0) {
            navigate(-1);
          } else {
            handlePrevious();
          }
        }}
        rightIcon={isEditMode ? "Trash2" : null}
        onRightPress={isEditMode ? deleteProduct : null}
        rightIconDisabled={isDeleting}
        showBackButton={true}
        backButtonText={currentStep === 0 ? 'Annuler' : 'Retour'}
      />
      
      <div className="create-listing-content">
        {/* Mode Édition Badge */}
        {isEditMode && (
          <div className="edit-mode-badge">
            <Icon name="Edit" size={14} color="white" />
            <span className="edit-mode-text">Mode Édition</span>
          </div>
        )}
        
        {/* Indicateur d'étapes */}
        {renderStepIndicator()}
        
        {/* Information de Progression */}
        <div className="progress-info">
          <p className="progress-text">
            Étape {currentStep + 1} sur {steps.length}
          </p>
          <p className="progress-percent">
            {Math.round(((currentStep + 1) / steps.length) * 100)}% complété
          </p>
        </div>
        
        {/* Barre de Progression linéaire */}
        <div className="progress-bar-container">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>
        
        {/* Avertissement userId manquant */}
        {!userId && (
          <div className="error-box">
            <Icon name="AlertTriangle" size={20} color="#FF3B30" />
            <p className="error-text">
              Vous devez être connecté pour créer une annonce
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/auth')}
              className="login-button"
            >
              Se connecter
            </Button>
          </div>
        )}
        
        {/* Contenu de l'Étape */}
        <div className="content-card">
          <div className="step-header">
            <h2 className="step-title">{steps[currentStep]?.title}</h2>
            <p className="step-description">
              {getStepDescription(steps[currentStep]?.id)}
            </p>
          </div>
          
          {renderStepContent()}
        </div>
        
        {/* Avertissement pour les champs obligatoires */}
        {Object.keys(validationErrors).length > 0 && (
          <div className="warning-box">
            <Icon name="AlertTriangle" size={20} color="#FF9500" />
            <div className="warning-content">
              <p className="warning-text">
                Veuillez corriger les erreurs suivantes :
              </p>
              <ul className="error-list">
                {Object.values(validationErrors).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
      
      {/* Boutons de Navigation */}
      <div className="navigation-buttons">
        <div className="nav-buttons-left">
          {currentStep > 0 && (
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={isSubmitting}
              leftIcon={<Icon name="ArrowLeft" size={16} color="#666" />}
              className="nav-button"
            >
              Précédent
            </Button>
          )}
          {currentStep === 0 && (
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
              leftIcon={<Icon name="X" size={16} color="#666" />}
              className="nav-button"
            >
              Annuler
            </Button>
          )}
        </div>
        
        <div className="nav-buttons-right">
          {currentStep === steps.length - 1 ? (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!userId || isSubmitting}
              loading={isSubmitting}
              leftIcon={<Icon name={isEditMode ? "Check" : "Upload"} size={16} color="white" />}
              className="submit-button"
            >
              {isEditMode 
                ? 'Mettre à jour' 
                : calculateTotalCost() > 0
                  ? 'Procéder au paiement' 
                  : 'Publier l\'annonce'
              }
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!userId || isSubmitting}
              rightIcon={<Icon name="ArrowRight" size={16} color="white" />}
              className="next-button"
            >
              Suivant
            </Button>
          )}
        </div>
      </div>
      
      {renderUploadProgress()}
      
      {/* Modal de Paiement (uniquement pour la création) */}
      {!isEditMode && (
        <PaymentModal
          visible={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          amount={paymentAmount}
          title="Paiement de l'annonce"
          description="Votre annonce sera publiée après confirmation du paiement"
          onPaymentSuccess={async (paymentData) => {
            setShowPaymentModal(false);
            await sendToBackend();
          }}
          onPaymentError={(error) => {
            console.error('Erreur paiement:', error);
            alert('Le paiement a échoué. Veuillez réessayer.');
          }}
        />
      )}
    </div>
  );
};

export default CreateListing;